import React from 'react'

import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { rem } from '@mantine/core'

import { AdBlockContainer } from 'components/molecules/adBlock/adBlockContainer'
import { CarePathSelection } from 'components/molecules/carePathSelection'
import { CarePathOptionProps } from 'components/molecules/carePathSelection/option'
import { MastheadAd } from 'components/pages/article/components/MastheadAd'
import { Props as DynamicSpotlightPageProps } from 'components/pages/dynamicSpotlight'
import { useTrackingClickCarePathTemplate } from 'hooks/useCustomEventTracking'
import { useDetectSCpage } from 'hooks/useDetectSCpage'
import { useIsMobile } from 'hooks/useIsMobile'
import { useMastheadAdEnable } from 'hooks/useMastheadAdEnable'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { LOCALE_SPECS } from 'interfaces/constants'
import { Article } from 'interfaces/types'
import { ArticleStoreProvider } from 'state/article'
import { getAdTag, getCountryAdTag } from 'utils/ads'

import { addPageCookies } from './helpers'
import { GTMScript } from '../appWrappers/GTMScript'
import { NavigatorLayout } from '../navigatorLayout'

export type Props = {
  isInline?: boolean
} & Partial<DynamicSpotlightPageProps>

const CarePathSectionTemplate = ({
  spotlight,
  footerInfo,
  dataNavMenuContent,
  isInline,
}: Props) => {
  const router = useRouterExtend()
  const { trackUserClickCarePathTemplate } = useTrackingClickCarePathTemplate()
  const {
    pathname,
    query: { slug },
    push,
    locale,
  } = router
  const isMobile = useIsMobile(true)
  const { isNestle, isNan, isVinamilk } = useDetectSCpage(spotlight?.keyID)

  const options = (spotlight?.listGroup || []).map((i) => ({
    label: i.name,
    icon: i.icon,
    groupId: i.groupId,
    bgColor: i.colorPatterns.primary,
    termId: i.termId,
  })) as CarePathOptionProps[]

  const carePathSelectionProps = {
    heading: spotlight?.titleGroup || '',
    description: spotlight?.descriptionGroup || '',
    options,
    onSelect: ({ groupId, termId }: CarePathOptionProps) => {
      slug?.[0] &&
        groupId &&
        addPageCookies({
          pageId: slug?.[0],
          audienceId: groupId,
          audienceTermId: termId,
        })
      trackUserClickCarePathTemplate(groupId)
      const hasPrefix = pathname === '/spotlight/[...slug]'
      hasPrefix
        ? push('/spotlight/[...slug]', `/spotlight/${slug}/${groupId}`)
        : push('[...slug]', `/${slug}/${groupId}`)
    },
  }
  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: {
      result: 'Spotlight',
      redundant: true,
    },
  })
  const mastheadEnable = useMastheadAdEnable(spotlight?.adsOnArticles || {})

  return (
    <>
      <GTMScript />
      <NavigatorLayout
        title={spotlight?.postTitle}
        description={spotlight?.postTitle}
        image={LOCALE_SPECS[locale].HOMEPAGE_IMAGE}
        isSponsored={spotlight?.isSponsor}
        sponsoredType="content"
        sponsoredBy={spotlight?.sponsor}
        // isHomePage={false}
        SEOData={{
          title: spotlight?.postTitle,
          description: spotlight?.postTitle,
          apiMetaData: spotlight?.metaTag,
          isIndexable: true,
        }}
        type="profile"
        footerInfo={footerInfo}
        languageOptions={spotlight?.translateOptions || []}
        dataNavMenuContent={dataNavMenuContent}
        isInline={isInline}
      >
        <ArticleStoreProvider
          init={{
            currentArticle: (spotlight as unknown) as Article,
          }}
        >
          <AdBlockContainer>
            <MastheadAd
              adUnitPath={adUnitPath}
              className={`spotlight-supercontent-masthead ${spotlight?.type}-masthead`}
              hasPlaceholder={mastheadEnable}
              containerClassName="z-[11]"
            />
          </AdBlockContainer>
          <CarePathSelection
            {...carePathSelectionProps}
            keyID={spotlight?.keyID}
            unsetRatio={isNestle || isNan}
            useCommonAssess={isNan || isNestle}
            assessFolderName={isNan ? 'nan' : isNestle ? 'nestle' : undefined}
            styles={(theme) => ({
              root: {
                ...(isNestle && {
                  minHeight: 'inherit',
                  backgroundImage: `url(${CommonGAssets.getAssetPath(
                    'nestle/selection_bg_desktop.jpg',
                  )})`,
                  backgroundSize: 'cover',
                }),
                ...(isNan && {
                  minHeight: `inherit`,
                  backgroundImage: `url(${CommonGAssets.getAssetPath(
                    'nan/selection_bg_desktop.jpg',
                  )})`,
                  backgroundPosition: 'bottom center',
                  backgroundSize: 'cover',
                  [theme.fn.smallerThan('sm')]: {
                    paddingTop: rem(40),
                    backgroundImage: `url(${CommonGAssets.getAssetPath(
                      'nan/selection_bg_mobile.jpg',
                    )})`,
                  },
                  [theme.fn.largerThan('sm')]: {
                    paddingLeft: rem(24),
                    paddingRight: rem(24),
                  },
                }),
              },
              wrapper: {
                ...(isNan && {
                  maxWidth: rem(881),
                  margin: '0 auto',
                  [theme.fn.largerThan('sm')]: {
                    paddingLeft: 0,
                    paddingRight: 0,
                    gap: rem(24),
                  },
                }),
              },
              optionList: {
                ...(isNan && {
                  [theme.fn.smallerThan('sm')]: {
                    flexDirection: 'row',
                  },
                  '.item': {
                    [theme.fn.largerThan('sm')]: {
                      width: 'auto',
                      maxWidth: rem(360),
                    },
                  },
                }),
              },
              radioOptionRoot: {
                ...(isNan && {
                  boxShadow: 'none',
                  border: 0,
                }),
                ...(isVinamilk && {
                  backgroundColor: '#fff',
                  borderColor: theme.colors.neutral[0],
                }),
              },
              optionHeading: {
                ...(isNan && {
                  display: 'none',
                }),
              },
              heading: {
                ...(isVinamilk && {
                  color: '#246222',
                }),
              },
            })}
          />
        </ArticleStoreProvider>
      </NavigatorLayout>
    </>
  )
}

export default CarePathSectionTemplate
